/**
 * Reporting Service
 *
 * API client for financial reports and analytics.
 */

import { get, post, buildQueryString, type ApiResponse, type PageInfo } from './api-client';
import { buildPaginationParams, buildPeriodParams } from '../utils/api-helpers';
import { formatMoney as formatMoneyUtil, formatDateRange } from '../utils/formatters';

import type { Money } from '../types/common';

// ============================================================================
// Types - Trial Balance
// ============================================================================

export interface TrialBalanceRow {
  accountId: string;
  accountCode: string;
  accountName: string;
  accountType: string;
  debitBalance: Money;
  creditBalance: Money;
}

/**
 * An account left out of `rows` (inactive and/or header) that still carries a
 * nonzero posted net balance — surfaced so a genuinely-balanced ledger
 * (`isBalanced` is computed independently of which accounts are displayed)
 * doesn't just look mysteriously off with no way to find why.
 */
export interface ExcludedAccountBalance {
  accountId: string;
  accountCode: string;
  accountName: string;
  isActive: boolean;
  isHeader: boolean;
  debitBalance: Money;
  creditBalance: Money;
}

export interface TrialBalanceReport {
  asOfDate: string;
  generatedAt: string;
  rows: TrialBalanceRow[];
  totalDebit: Money;
  totalCredit: Money;
  isBalanced: boolean;
  excludedAccounts?: ExcludedAccountBalance[];
}

export interface TrialBalanceFilter {
  asOfDate?: string;
  includeZeroBalances?: boolean;
  includeInactiveAccounts?: boolean;
  maxDepth?: number;
}

// ============================================================================
// Types - Income Statement
// ============================================================================

export interface IncomeStatementRow {
  accountId: string;
  accountCode: string;
  accountName: string;
  amount: Money;
  priorAmount?: Money;
}

export interface IncomeStatementSection {
  sectionType: string;
  sectionName: string;
  rows: IncomeStatementRow[];
  sectionTotal: Money;
}

export interface IncomeStatementReport {
  periodStart: string;
  periodEnd: string;
  generatedAt: string;
  sections: IncomeStatementSection[];
  totalRevenue: Money;
  totalExpenses: Money;
  netIncome: Money;
  isProfit: boolean;
  priorNetIncome?: Money;
  variancePercentage?: string;
}

export interface IncomeStatementFilter {
  startDate: string;
  endDate: string;
  comparative?: boolean;
  maxDepth?: number;
}

// ============================================================================
// Types - Balance Sheet
// ============================================================================

export interface BalanceSheetRow {
  accountId: string;
  accountCode: string;
  accountName: string;
  balance: Money;
  priorBalance?: Money;
}

export interface BalanceSheetCategory {
  categoryName: string;
  rows: BalanceSheetRow[];
  categoryTotal: Money;
}

export interface BalanceSheetSection {
  sectionName: string;
  categories: BalanceSheetCategory[];
  sectionTotal: Money;
}

export interface BalanceSheetReport {
  asOfDate: string;
  generatedAt: string;
  assets: BalanceSheetSection;
  liabilities: BalanceSheetSection;
  equity: BalanceSheetSection;
  totalAssets: Money;
  totalLiabilitiesAndEquity: Money;
  isBalanced: boolean;
}

export interface BalanceSheetFilter {
  asOfDate?: string;
  comparative?: boolean;
  maxDepth?: number;
}

// ============================================================================
// Types - General Ledger
// ============================================================================

export interface GeneralLedgerEntry {
  entryDate: string;
  entryNumber: string;
  journalEntryId: string;
  memo: string;
  reference: string;
  debit: Money;
  credit: Money;
  balance: Money;
}

export interface GeneralLedgerAccount {
  accountId: string;
  accountCode: string;
  accountName: string;
  openingBalance: Money;
  periodDebit: Money;
  periodCredit: Money;
  closingBalance: Money;
  entries: GeneralLedgerEntry[];
}

export interface GeneralLedgerResponse {
  accounts: GeneralLedgerAccount[];
  pageInfo: PageInfo;
}

export interface GeneralLedgerFilter {
  startDate: string;
  endDate: string;
  accountId?: string;
  projectId?: string;
  costCenterId?: string;
}

// ============================================================================
// Types - Account Activity
// ============================================================================

export interface AccountActivityPeriod {
  periodName: string;
  startDate: string;
  endDate: string;
  debit: Money;
  credit: Money;
  netChange: Money;
  endingBalance: Money;
}

export interface AccountActivityReport {
  accountId: string;
  accountName: string;
  openingBalance: Money;
  totalDebit: Money;
  totalCredit: Money;
  closingBalance: Money;
  periods: AccountActivityPeriod[];
}

export interface AccountActivityFilter {
  startDate: string;
  endDate: string;
  groupByMonth?: boolean;
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Get trial balance report.
 */
export async function getTrialBalance(
  orgId: string,
  filter?: TrialBalanceFilter
): Promise<ApiResponse<TrialBalanceReport>> {
  const query = buildQueryString({
    asOfDate: filter?.asOfDate,
    includeZeroBalances: filter?.includeZeroBalances,
    includeInactiveAccounts: filter?.includeInactiveAccounts,
    maxDepth: filter?.maxDepth,
  });
  return get<TrialBalanceReport>(`/organizations/${orgId}/reports/trial-balance${query}`);
}

/**
 * Get income statement (P&L) report.
 */
export async function getIncomeStatement(
  orgId: string,
  filter: IncomeStatementFilter
): Promise<ApiResponse<IncomeStatementReport>> {
  const query = buildQueryString({
    ...buildPeriodParams(filter.startDate, filter.endDate),
    comparative: filter.comparative,
    maxDepth: filter.maxDepth,
  });
  return get<IncomeStatementReport>(`/organizations/${orgId}/reports/income-statement${query}`);
}

/**
 * Get balance sheet report.
 */
export async function getBalanceSheet(
  orgId: string,
  filter?: BalanceSheetFilter
): Promise<ApiResponse<BalanceSheetReport>> {
  const query = buildQueryString({
    asOfDate: filter?.asOfDate,
    comparative: filter?.comparative,
    maxDepth: filter?.maxDepth,
  });
  return get<BalanceSheetReport>(`/organizations/${orgId}/reports/balance-sheet${query}`);
}

/**
 * Get general ledger report.
 */
export async function getGeneralLedger(
  orgId: string,
  filter: GeneralLedgerFilter,
  pageSize?: number,
  pageToken?: string
): Promise<ApiResponse<GeneralLedgerResponse>> {
  const query = buildQueryString({
    ...buildPeriodParams(filter.startDate, filter.endDate),
    ...buildPaginationParams(pageSize, pageToken),
    accountId: filter.accountId,
    projectId: filter.projectId,
    costCenterId: filter.costCenterId,
  });
  return get<GeneralLedgerResponse>(`/organizations/${orgId}/reports/general-ledger${query}`);
}

/**
 * Get account activity report.
 */
export async function getAccountActivity(
  orgId: string,
  accountId: string,
  filter: AccountActivityFilter
): Promise<ApiResponse<AccountActivityReport>> {
  const query = buildQueryString({
    ...buildPeriodParams(filter.startDate, filter.endDate),
    groupByMonth: filter.groupByMonth,
  });
  return get<AccountActivityReport>(`/organizations/${orgId}/accounts/${accountId}/activity${query}`);
}

// ============================================================================
// Types - Entity Balance
// ============================================================================

export interface EntityBalanceLedgerRow {
  ledgerId: string;
  ledgerName: string;
  ledgerCode: string;
  accountId: string;
  accountCode: string;
  accountName: string;
  accountType: string;
  balance: Money;
}

export interface EntityBalanceReport {
  entityId: string;
  asOfDate: string;
  generatedAt: string;
  rows: EntityBalanceLedgerRow[];
  totalBalance: Money;
}

/**
 * Get entity balance — aggregate account balances across all ledgers.
 */
/* v8 ignore next 8 */
export async function getEntityBalance(
  orgId: string,
  entityId: string,
  asOfDate?: string
): Promise<ApiResponse<EntityBalanceReport>> {
  const query = buildQueryString({ asOfDate });
  return get<EntityBalanceReport>(`/organizations/${orgId}/entities/${entityId}/balance${query}`);
}

export interface EntityBatchBalanceResult {
  balances: Record<string, { amount: number; currency: string }>;
}

/**
 * Get total balance for multiple entities in a single request.
 */
/* v8 ignore next 5 */
export async function getEntityBatchBalance(
  orgId: string,
  entityIds: string[],
  asOfDate?: string
): Promise<ApiResponse<EntityBatchBalanceResult>> {
  return post<EntityBatchBalanceResult>(`/organizations/${orgId}/entities/batch-balance`, { entityIds, asOfDate: asOfDate ?? '' });
}

// ============================================================================
// Types - Unbalanced Entries
// ============================================================================

/**
 * A journal entry whose lines' base-currency debits and credits don't sum
 * to the same total. Every JE creation path validates balance before
 * persisting, so this should normally never appear — it's a safety-net
 * report for reviewing and correcting anything that shouldn't exist.
 */
export interface UnbalancedEntryRow {
  id: string;
  entryNumber: string;
  entryDate: string;
  memo: string;
  status: string;
  totalDebit: Money;
  totalCredit: Money;
  difference: Money;
}

export interface UnbalancedEntriesReport {
  entries: UnbalancedEntryRow[];
}

/**
 * Get journal entries whose debit/credit totals don't match.
 */
export async function getUnbalancedEntries(
  orgId: string
): Promise<ApiResponse<UnbalancedEntriesReport>> {
  return get<UnbalancedEntriesReport>(`/organizations/${orgId}/reports/unbalanced-entries`);
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Format a money amount for display.
 * Uses the currency's default locale for formatting unless overridden.
 */
export function formatMoney(money: Money, locale?: string): string {
  return formatMoneyUtil(money, locale ? { locale } : {});
}

/**
 * Calculate the variance between two amounts.
 */
export function calculateVariance(current: number, prior: number): number {
  if (prior === 0) {return current === 0 ? 0 : 100;}
  return ((current - prior) / Math.abs(prior)) * 100;
}

/**
 * Format variance as a percentage string.
 */
export function formatVariance(variance: number): string {
  const sign = variance >= 0 ? '+' : '';
  return `${sign}${variance.toFixed(1)}%`;
}

/**
 * Get report period display string.
 */
export function formatReportPeriod(startDate: string, endDate: string): string {
  return formatDateRange(startDate, endDate, { locale: 'en-IN', style: 'medium' });
}

/**
 * Get current fiscal year dates based on fiscal year start month.
 * @param fiscalYearStartMonth - Month when fiscal year starts ('01', '04', '07', '10')
 */
export function getCurrentFiscalYear(fiscalYearStartMonth: string = '01'): { startDate: string; endDate: string } {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth(); // 0-indexed
  const fyStartMonth = parseInt(fiscalYearStartMonth, 10) - 1; // Convert to 0-indexed

  // Determine fiscal year start year
  const fiscalYearStart = currentMonth >= fyStartMonth ? currentYear : currentYear - 1;
  const fiscalYearEnd = fiscalYearStart + (fyStartMonth === 0 ? 0 : 1);

  // Calculate end month (month before start month of next FY)
  const endMonth = fyStartMonth === 0 ? 12 : fyStartMonth;
  const endYear = fyStartMonth === 0 ? fiscalYearStart : fiscalYearEnd;

  // Get last day of end month
  const lastDay = new Date(endYear, endMonth, 0).getDate();

  return {
    startDate: `${fiscalYearStart}-${fiscalYearStartMonth}-01`,
    endDate: `${endYear}-${String(endMonth).padStart(2, '0')}-${lastDay}`,
  };
}

function formatDateStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function getWeekStart(now: Date): string {
  const start = new Date(now)
  start.setDate(now.getDate() - now.getDay())
  return formatDateStr(start)
}

function getMonthStart(year: number, month: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}-01`
}

function getQuarterStart(year: number, month: number): string {
  const qStart = Math.floor(month / 3) * 3
  return `${year}-${String(qStart + 1).padStart(2, '0')}-01`
}

/** Get date range for common report periods. */
export function getReportPeriod(
  period: 'today' | 'this_week' | 'this_month' | 'this_quarter' | 'this_year' | 'ytd',
  fiscalYearStartMonth: string = '01'
): { startDate: string; endDate: string } {
  const now = new Date()
  const today = formatDateStr(now)
  if (period === 'today') { return { startDate: today, endDate: today } }
  if (period === 'this_week') { return { startDate: getWeekStart(now), endDate: today } }
  if (period === 'this_month') { return { startDate: getMonthStart(now.getFullYear(), now.getMonth()), endDate: today } }
  if (period === 'this_quarter') { return { startDate: getQuarterStart(now.getFullYear(), now.getMonth()), endDate: today } }
  if (period === 'this_year') { return { startDate: `${now.getFullYear()}-01-01`, endDate: today } }
  if (period === 'ytd') { return getCurrentFiscalYear(fiscalYearStartMonth) }
  return { startDate: today, endDate: today }
}
